"use client";

import { useCallback, useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import type { JobAlertPayload } from "@/lib/marketplace/job-alert-payload";
import { playJobAlertLoop, stopJobAlertLoop } from "./job-alert-sound";

export function JobAlertModal() {
  const { data: session } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [payload, setPayload] = useState<JobAlertPayload | null>(null);
  const companyId = session?.user?.companyId;
  const jobAlertId = searchParams.get("jobAlert");

  const dismiss = useCallback(() => {
    stopJobAlertLoop();
    setPayload(null);
    if (typeof window !== "undefined") {
      const u = new URL(window.location.href);
      if (u.searchParams.has("jobAlert")) {
        u.searchParams.delete("jobAlert");
        const next = u.search ? `${u.pathname}${u.search}` : u.pathname;
        router.replace(next);
      }
    }
  }, [router]);

  const showAlert = useCallback((jobAlert: JobAlertPayload) => {
    setPayload(jobAlert);
    playJobAlertLoop();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "JOB_ALERT_SHOW" && data.jobAlert) {
        showAlert(data.jobAlert as JobAlertPayload);
        return;
      }
      if (data.type === "JOB_ALERT_PLAY") {
        playJobAlertLoop();
        return;
      }
      if (data.type === "JOB_ALERT_STOP") {
        dismiss();
      }
    };

    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [showAlert, dismiss]);

  useEffect(() => {
    if (!companyId || !jobAlertId || payload?.jobId === jobAlertId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/marketplace/jobs/${jobAlertId}/alert-summary`);
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data.jobAlert) {
          showAlert(data.jobAlert as JobAlertPayload);
        }
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, jobAlertId, payload?.jobId, showAlert]);

  useEffect(() => {
    return () => stopJobAlertLoop();
  }, []);

  useEffect(() => {
    if (!payload) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [payload]);

  if (!companyId || !payload) return null;

  const title = payload.reference?.trim() || payload.address;

  function quoteNow() {
    dismiss();
    router.push(payload!.quoteNowUrl);
  }

  function quoteLater() {
    dismiss();
    router.push(payload!.quoteLaterUrl);
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="job-alert-title"
    >
      <div className="relative flex max-h-[min(90vh,640px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl">
        <div className="border-b border-zinc-100 bg-brand-dark px-5 py-4 pr-12 text-white">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-yellow">New job near you</p>
          <h2 id="job-alert-title" className="mt-1 text-lg font-bold leading-snug">
            {title}
          </h2>
          <p className="mt-1 text-sm text-zinc-300">
            {payload.postcode} · {payload.jobTypeLabel} · {payload.timingLabel}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {payload.reference && (
            <p className="text-sm text-zinc-600">
              <span className="font-medium text-zinc-800">Reference:</span> {payload.reference}
            </p>
          )}
          {payload.reference ? (
            <p className="mt-2 text-sm text-zinc-600">
              <span className="font-medium text-zinc-800">Site:</span> {payload.address}
            </p>
          ) : null}

          {payload.summaryLines.length > 0 ? (
            <dl className="mt-4 space-y-2">
              {payload.summaryLines.map((line) => (
                <div key={line.label} className="flex gap-3 text-sm">
                  <dt className="w-28 shrink-0 text-zinc-500">{line.label}</dt>
                  <dd className="font-medium text-zinc-900">{line.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}

          <p className="mt-4 text-sm text-zinc-600">
            Quote while it&apos;s fresh — customers often choose from the first competitive prices they receive.
          </p>
        </div>

        <div className="flex flex-col gap-2 border-t border-zinc-100 bg-zinc-50 px-5 py-4 sm:flex-row">
          <button
            type="button"
            onClick={quoteNow}
            className="flex-1 rounded-xl bg-brand-yellow px-4 py-3 text-sm font-bold text-brand-dark hover:bg-brand-yellow/90"
          >
            Quote now
          </button>
          <button
            type="button"
            onClick={quoteLater}
            className="flex-1 rounded-xl border border-zinc-300 bg-white px-4 py-3 text-sm font-semibold text-zinc-800 hover:bg-zinc-50"
          >
            Quote later
          </button>
        </div>

        <button
          type="button"
          onClick={dismiss}
          className="absolute right-3 top-3 rounded-lg p-2 text-zinc-400 hover:bg-white/10 hover:text-white"
          aria-label="Dismiss alert"
        >
          <span className="text-xl leading-none" aria-hidden>
            ×
          </span>
        </button>
      </div>
    </div>
  );
}
